import dayjs, { Dayjs } from 'dayjs'
import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { stockOnHandService } from '$services'
import { exceptionMessageHandler } from '$utils'
import { IStockOnHandLine } from '$models'
import { StocktakeTemplate } from 'src/models/interfaces/stock/stocktakeTemplate'

interface StockOnHandState {
  stockOnHandReport: StockOnHandReportPresentation | undefined
  stocktakeTemplates: StocktakeTemplate[] | undefined
}

interface GroupedStockOnHand {
  header: string
  lines: IStockOnHandLine[]
  valueOnHandGroupTotal: number
}

export type StockOnHandReportPresentation = {
  groups: GroupedStockOnHand[]
  valueOnHandGrandTotal: number
  reportAt: Dayjs | undefined
}

export interface StockOnHandSlice extends StockOnHandState {
  resetStockOnHandSlice: () => void

  populateStockOnHandReport: (
    templateId: string,
    reportDateTime: dayjs.Dayjs
  ) => Promise<void>

  setStockOnHandReport: (
    stockOnHandReport: StockOnHandReportPresentation | undefined
  ) => void

  getStocktakeTemplates: (includeDeleted: boolean) => Promise<{
    stocktakeTemplateList: StocktakeTemplate[] | null
  }>
}

const initialState: StockOnHandState = {
  stockOnHandReport: undefined,
  stocktakeTemplates: undefined,
}

const createStockOnHandSlice: StateCreator<
  StockOnHandSlice & SharedSlice,
  [],
  [],
  StockOnHandSlice
> = (set) => ({
  ...initialState,

  resetStockOnHandSlice: () => set(initialState),

  populateStockOnHandReport: async (
    templateId: string,
    reportDateTime: dayjs.Dayjs
  ): Promise<void> => {
    try {
      const stockOnHandReport = await stockOnHandService.getStockOnHand(
        templateId,
        undefined,
        reportDateTime
      )

      const stockOnHandLines = stockOnHandReport?.lines

      if (stockOnHandLines) {
        // Group stock lines by header (category) and calculate group totals
        const groupedStockOnHandLines: Record<string, GroupedStockOnHand> =
          stockOnHandLines.reduce((groups, line) => {
            const groupKey = line.header ?? 'Uncategorized'

            if (!groups[groupKey]) {
              groups[groupKey] = {
                lines: [],
                valueOnHandGroupTotal: 0,
              }
            }

            groups[groupKey].lines.push(line)
            groups[groupKey].valueOnHandGroupTotal += line.valueOnHand ?? 0

            return groups
          }, {})

        // Convert the grouped object into an array for easier rendering
        const groupedArray = Object.entries(groupedStockOnHandLines).map(
          ([header, { lines, valueOnHandGroupTotal }]) => ({
            header,
            lines,
            valueOnHandGroupTotal,
          })
        )

        const valueOnHandGrandTotal = groupedArray.reduce(
          (sum, group) => sum + group.valueOnHandGroupTotal,
          0
        )

        const reportUI: StockOnHandReportPresentation = {
          groups: groupedArray,
          valueOnHandGrandTotal,
          reportAt: stockOnHandReport.reportAt,
        }

        // Note: We deliberately don't expose a setter function outside this slice
        // to maintain data encapsulation. Components can only access the stockOnHandReport
        // but cannot directly modify the data
        set(() => ({ stockOnHandReport: reportUI }))
      } else {
        set(() => ({ stockOnHandReport: undefined }))
      }
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve stock on hand report')
      set(() => ({ stockOnHandReport: undefined }))
    }
  },

  setStockOnHandReport: (
    stockOnHandReport: StockOnHandReportPresentation | undefined
  ) => set(() => ({ stockOnHandReport: stockOnHandReport })),

  getStocktakeTemplates: async (includeDeleted: boolean) => {
    try {
      const stocktakeTemplateList =
        await stockOnHandService.getAllStocktakeTemplates(includeDeleted)

      // Note: We deliberately don't expose a setter function outside this slice
      // to maintain data encapsulation. Components can only access the stocktakeTemplates
      // but cannot directly modify the data
      set(() => ({ stocktakeTemplates: stocktakeTemplateList }))

      return { stocktakeTemplateList: stocktakeTemplateList }
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve stocktake templates')
      return { stocktakeTemplateList: null }
    }
  },
})

export default createStockOnHandSlice
